import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Wallet,
  ArrowDownToLine,
  Repeat,
  Send,
  Users as UsersIcon,
  ShoppingCart,
  Ticket,
  Bell,
  User,
  UserPlus,
  Trophy,
  Sprout,
  Handshake,
  Sparkles,
  LifeBuoy,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  Gift,
  ArrowLeft,
  Home,
  ArrowRightLeft,
  TrendingUp,
  Store,
  Package,
  ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/user/dashboard', icon: LayoutDashboard },
  { name: 'Wallets', href: '/user/wallets', icon: Wallet },
  { name: 'Deposit', href: '/user/deposit', icon: ArrowDownToLine },
  { name: 'Swap', href: '/user/swap', icon: Repeat },
  { name: 'Exchange', href: '/user/exchange', icon: ArrowRightLeft },
  { name: 'Withdraw', href: '/user/withdraw', icon: Send },
  { name: 'Transfer', href: '/user/transfer', icon: UsersIcon },
  { section: 'Investments & Savings' },
  { name: 'Savings Plans', href: '/user/savings', icon: Sprout },
  { name: 'Growth Packages', href: '/user/growth-packages', icon: TrendingUp },
  { name: 'My Growth Packages', href: '/user/my-growth-packages', icon: TrendingUp },
  { section: 'Community & Rewards' },
  { name: 'Community Savings', href: '/user/groups', icon: Handshake },
  { name: 'Rewards & Tasks', href: '/user/tasking', icon: Sparkles },
  { name: 'Predictions', href: '/user/predictions', icon: Trophy },
  { name: 'Refer & Earn', href: '/user/referrals', icon: Gift },
  { name: 'Creator Earnings', href: '/creator/earnings', icon: Sparkles },
  { name: 'Referral Income', href: '/referral/earnings', icon: TrendingUp },
  { section: 'Commerce' },
  { name: 'Marketplace', href: '/commerce/marketplace', icon: Store },
  { name: 'My Store', href: '/commerce/dashboard', icon: Package },
  { name: 'My Orders', href: '/commerce/orders', icon: ShoppingBag },
  { name: 'My Vouchers', href: '/user/vouchers', icon: Ticket },
  { section: 'Account' },
  { name: 'Contacts', href: '/user/contacts', icon: UserPlus },
  { name: 'Notifications', href: '/user/notifications', icon: Bell },
  { name: 'Support', href: '/user/support', icon: LifeBuoy },
  { name: 'Profile', href: '/user/profile', icon: User },
];

export const UserLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow border-r border-border bg-surface pt-5 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6 mb-4">
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="user-logo">
              Matrise
            </h1>
          </div>
          
          {/* Back to Super App Button */}
          <div className="px-3 mb-4">
            <Button
              onClick={() => navigate('/app/home')}
              variant="outline"
              className="w-full justify-start"
              data-testid="back-to-super-app"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Super App
            </Button>
          </div>
          
          <div className="flex-1 flex flex-col">
            <nav className="flex-1 px-3 space-y-1">
              {navigation.map((item, index) => {
                // Render section headers
                if (item.section) {
                  return (
                    <div key={`section-${index}`} className="pt-4 pb-2">
                      <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {item.section}
                      </h3>
                    </div>
                  );
                }
                
                // Render navigation items
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'
                    }`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-border p-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-foreground">{user?.name || user?.email}</p>
                  <p className="text-xs text-muted-foreground">User</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  data-testid="theme-toggle"
                  className="h-8 w-8"
                >
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="user-logout-button"
                  className="h-8 w-8 text-red-600 hover:text-red-700"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="md:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-surface border-b border-border md:hidden">
          <button
            type="button"
            className="px-4 text-muted-foreground focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="flex-1 flex justify-between items-center px-4">
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MTR
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-8 w-8"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-surface border-b border-border">
            <nav className="px-2 py-2 space-y-1">
              {/* Back to Super App Button - Mobile */}
              <Button
                onClick={() => {
                  navigate('/app/home');
                  setMobileMenuOpen(false);
                }}
                variant="outline"
                className="w-full justify-start mb-2"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Super App
              </Button>
              
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full group flex items-center px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </button>
            </nav>
          </div>
        )}

        <main className="flex-1 p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
